/**
 * "How is the editor arranged right now" — two booleans, for the lifetime of the
 * mounted editor (ADR-5A).
 *
 * Collapsing a panel is a **viewing** gesture, not an editing one: it changes no
 * card data, writes nothing through the repository port, and fires no autosave. So
 * the state lives here beside `useInspector` rather than on the card document —
 * the same posture ADR-2D and ADR-3B already set for the field search and the
 * inspector target.
 *
 * It survives every editing interaction because the editor stays mounted across
 * all of them (spec R78), and it is deliberately **not** persisted: no storage
 * key, no URL parameter, nothing in the card. Leaving the editor discards it and
 * both panels come back expanded, which is exactly what R78 asks for.
 */

import { useCallback, useState } from 'react';

export interface EditorChromeState {
  /** True while the configuration pane is collapsed to zero width (spec R75). */
  paneCollapsed: boolean;
  /** True while the properties panel is collapsed to zero width (spec R76). */
  panelCollapsed: boolean;
  togglePane: () => void;
  togglePanel: () => void;
}

export const useEditorChrome = (): EditorChromeState => {
  // Both panels start expanded, on arrival and on every return to the editor.
  const [paneCollapsed, setPaneCollapsed] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // Two independent axes: collapsing one panel says nothing about the other.
  const togglePane = useCallback(() => setPaneCollapsed((collapsed) => !collapsed), []);
  const togglePanel = useCallback(() => setPanelCollapsed((collapsed) => !collapsed), []);

  return { paneCollapsed, panelCollapsed, togglePane, togglePanel };
};
